import React from 'react';
import { HoverTooltip } from 'components/common/HoverTooltip';
import {
  ArrayFieldTemplateProps,
  ArrayFieldTemplateItemType,
  getUiOptions,
} from '@rjsf/utils';
import { useTranslation } from 'react-i18next';
import { Icon } from 'react-icons-kit';
import { ic_keyboard_arrow_down } from 'react-icons-kit/md/ic_keyboard_arrow_down';
import { ic_keyboard_arrow_up } from 'react-icons-kit/md/ic_keyboard_arrow_up';
import { Delete } from 'components/common/ProjectIcons';
import { buttonClasses } from 'components/common/tailwindClassnames';
import { hasScalarListItems, isBlankListItem } from './blankListItems';
import { RowLayoutContext, rowCellClass, rowColumns } from './rowLayout';

// A quarter smaller than the 30px default: in a list row it sits beside the
// move arrows and should not outweigh them. Shared with CustomArrayField.
export const LIST_DELETE_ICON_SIZE = 22.5;

// Template for rjsf-rendered arrays (arrays of objects — see generateUISchema's
// hasScalarItems gate; scalar arrays go through CustomArrayField instead).
// Lays the title and description full-width above the item list so nested
// arrays keep the whole card width instead of halving it at every level.
const CustomArrayFieldTemplate = (props: ArrayFieldTemplateProps) => {
  const {
    title,
    schema,
    items,
    canAdd,
    onAddClick,
    disabled,
    readonly,
    required,
    formData,
    uiSchema,
    registry,
  } = props;
  const { t } = useTranslation();
  const showAdd = canAdd && !readonly;
  // One empty row at a time: another Add would only stack blank rows the
  // user then has to delete (they are dropped on save, but read as errors
  // until then). Reaches plain lists nested in object rows; top-level ones
  // render through CustomArrayField, and reference lists as one multi-select.
  // Lists of objects are left alone — a row there is a set of fields with
  // their own defaults, not a single value.
  const hasBlankRow =
    hasScalarListItems(schema.items) &&
    Array.isArray(formData) &&
    formData.some(isBlankListItem);
  // Opt-in one-row-per-item layout; see rowLayout.ts. Null keeps the stacked
  // layout, including when the items don't fit in a row.
  const columns =
    getUiOptions(uiSchema).layout === 'row'
      ? rowColumns(schema, uiSchema, registry)
      : null;
  // Object items open with a legend band (index.css); their toolbar sits
  // inside the band's right end instead of beside it.
  const toolbarInBand = !columns && !hasScalarListItems(schema.items);
  const showToolbar = !readonly && items.some((item) => item.hasToolbar);
  const showMoves = items.some((item) => item.hasMoveUp || item.hasMoveDown);
  const showRemove = items.some((item) => item.hasRemove);

  return (
    <div className="my-2 w-full">
      {/* The Add button sits beside the title and description, centred on
                them, the same way CustomArrayField places it for scalar lists. */}
      {(title || schema.description || showAdd) && (
        <div className="mb-2 flex flex-row items-center justify-between gap-6">
          {/* Half the row for title and description, like every other
                        field (HorizontalFieldTemplate's 50/50 split). */}
          <div className="flex w-1/2 min-w-0 flex-col">
            {title && (
              <label className="font-semibold">
                {title}
                {required && (
                  <span className="ml-1 font-normal text-ui-status-error-destructive">
                    *
                  </span>
                )}
              </label>
            )}
            {schema.description && (
              <span className="mt-1 text-sm text-ui-text-primary-text">
                {schema.description}
              </span>
            )}
          </div>
          {showAdd && (
            <div className="flex w-1/2 min-w-0 justify-end">
              <button
                type="button"
                className={`${buttonClasses.listAdd} shrink-0`}
                disabled={disabled || hasBlankRow}
                title={
                  hasBlankRow
                    ? t('Fill in or remove the empty row first.')
                    : undefined
                }
                onClick={onAddClick}
                data-testid="CustomArrayFieldTemplate-add-button"
              >
                {t('Add Item')}
              </button>
            </div>
          )}
        </div>
      )}
      {/* A rail down the left joins the items to the title above, so a
                list nested in a list still reads as belonging to its parent. */}
      <div
        className={items.length > 0 ? 'ml-1 border-l-2 border-teal-3 pl-3' : ''}
        data-testid="CustomArrayFieldTemplate-items"
      >
        {columns && items.length > 0 && (
          <div
            className="mb-1 hidden w-full flex-row items-end gap-2 sm:flex"
            data-testid="CustomArrayFieldTemplate-row-header"
          >
            <div className="flex min-w-0 flex-1 flex-row gap-2">
              {columns.map((column, index) => (
                <div
                  key={column.name}
                  className={`${rowCellClass(index, columns.length)} px-2 text-sm font-semibold`}
                  data-testid="CustomArrayFieldTemplate-row-caption"
                >
                  {column.title}
                  {column.required && (
                    <span
                      className="ml-1 font-normal text-ui-status-error-destructive"
                      data-testid="CustomArrayFieldTemplate-row-required"
                    >
                      *
                    </span>
                  )}
                </div>
              ))}
            </div>
            {/* An invisible copy of the item toolbar, so the captions end where
                        the item cells do. */}
            {showToolbar && (
              <div
                className="invisible flex shrink-0 flex-row items-center"
                aria-hidden="true"
              >
                {showMoves && (
                  <>
                    <span>
                      <Icon icon={ic_keyboard_arrow_up} size={24} />
                    </span>
                    <span>
                      <Icon icon={ic_keyboard_arrow_down} size={24} />
                    </span>
                  </>
                )}
                {showRemove && (
                  <span>
                    <Delete size={LIST_DELETE_ICON_SIZE} />
                  </span>
                )}
              </div>
            )}
          </div>
        )}
        {items.map(
          ({
            key,
            index,
            children,
            hasToolbar,
            hasMoveUp,
            hasMoveDown,
            hasRemove,
            onReorderClick,
            onDropIndexClick,
          }: ArrayFieldTemplateItemType) => (
            <div
              key={key}
              className={`${
                columns
                  ? 'mb-3 flex w-full flex-col gap-2 transition-all duration-300 sm:flex-row sm:items-start'
                  : `mb-3 flex w-full flex-row items-start gap-2 transition-all duration-300 ${toolbarInBand ? 'relative' : ''}`
              } p-2`}
              data-testid={`CustomArrayFieldTemplate-item-${index}`}
            >
              {columns ? (
                <div className="min-w-0 sm:flex-1">
                  <RowLayoutContext.Provider value="item">
                    {children}
                  </RowLayoutContext.Provider>
                </div>
              ) : (
                <div className="min-w-0 flex-1">{children}</div>
              )}
              {/* Pinned to the top of the item so it stays beside the row it
                        moves however tall the item grows: over the right end of
                        the title band for object items, a flex sibling otherwise. */}
              {hasToolbar && !readonly && (
                <div
                  className={
                    columns
                      ? 'flex shrink-0 flex-row items-center justify-end sm:mt-2 sm:min-h-[2.375rem]'
                      : toolbarInBand
                        ? 'absolute right-3 top-2 flex h-8 flex-row items-center'
                        : 'flex shrink-0 flex-row items-center'
                  }
                >
                  {(hasMoveUp || hasMoveDown) && (
                    <>
                      <HoverTooltip
                        content={t('Move up') || 'Move up'}
                        position="top"
                        focusable={false}
                      >
                        <button
                          type="button"
                          className="rounded p-0 text-greyscale-5 hover:bg-greyscale-10 disabled:cursor-not-allowed disabled:opacity-30"
                          aria-label={t('Move up') || 'Move up'}
                          disabled={disabled || !hasMoveUp}
                          onClick={onReorderClick(index, index - 1)}
                        >
                          <Icon icon={ic_keyboard_arrow_up} size={24} />
                        </button>
                      </HoverTooltip>
                      <HoverTooltip
                        content={t('Move down') || 'Move down'}
                        position="top"
                        focusable={false}
                      >
                        <button
                          type="button"
                          className="rounded p-0 text-greyscale-5 hover:bg-greyscale-10 disabled:cursor-not-allowed disabled:opacity-30"
                          aria-label={t('Move down') || 'Move down'}
                          disabled={disabled || !hasMoveDown}
                          onClick={onReorderClick(index, index + 1)}
                        >
                          <Icon icon={ic_keyboard_arrow_down} size={24} />
                        </button>
                      </HoverTooltip>
                    </>
                  )}
                  {hasRemove && (
                    <HoverTooltip
                      content={t('Remove') || 'Remove'}
                      position="top"
                      focusable={false}
                    >
                      <button
                        type="button"
                        className="rounded p-0 hover:bg-greyscale-10 disabled:cursor-not-allowed disabled:opacity-30"
                        aria-label={t('Remove') || 'Remove'}
                        disabled={disabled}
                        onClick={onDropIndexClick(index)}
                      >
                        <Delete size={LIST_DELETE_ICON_SIZE} />
                      </button>
                    </HoverTooltip>
                  )}
                </div>
              )}
            </div>
          ),
        )}
      </div>
    </div>
  );
};

export default CustomArrayFieldTemplate;
